import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faTrash,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { classNames } from 'utils';

function getRequirementDescription(courses, courseLists) {
  if (!Array.isArray(courses) || courses.length === 0) return '(No courses)';

  return courses
    .map((course) => {
      const parts = [];
      if (course.connector) parts.push(course.connector);
      if (course.subject) parts.push(course.subject);
      if (course.number) parts.push(course.number);
      if (course.description) parts.push(course.description);
      if (course.proposedTitle)
        parts.push(`${course.proposedTitle} (Proposed)`);
      if (course.division) {
        const divLabel =
          course.division === 'Upper' || course.division === 'Lower'
            ? `${course.division} Division`
            : `${course.division}-Level`;
        parts.push(divLabel);
      }
      if (
        course.itemType === 'req_elective' ||
        course.itemType === 'req_subject'
      ) {
        parts.push('Elective');
      }
      if (course.itemType === 'req_gs' && course.gs) {
        parts.push(`(${course.gs})`);
      }
      if (courseLists && courseLists[course.courseListId]) {
        parts.push(
          `List: ${courseLists[course.courseListId]?.courseListName || 'Untitled course list'}`
        );
      }
      if (course.customText) parts.push(course.customText);
      return parts.filter(Boolean).join(' ');
    })
    .filter(Boolean)
    .join(', ');
}

function SubsectionRow({ item, index, courseLists, historyDeleted }) {
  const [expanded, setExpanded] = useState(false);

  const beforeObject = useMemo(() => {
    try {
      return typeof item.before === 'string'
        ? JSON.parse(item.before)
        : item.before || {};
    } catch (error) {
      console.error(`Error parsing item.before at index ${index}:`, error);
      return null;
    }
  }, [item.before, index]);

  const requirements = beforeObject?.requirements || [];
  const subsectionId = beforeObject?.subsectionId || '';

  // Pre-parse previously-deleted requirements so the JSX stays clean
  const parsedPreviouslyDeleted = useMemo(() => {
    const raw = historyDeleted?.[subsectionId] || [];
    return raw.reduce((acc, deletedItem) => {
      try {
        const parsed =
          typeof deletedItem.before === 'string'
            ? JSON.parse(deletedItem.before)
            : deletedItem.before || {};
        acc.push({
          courses: parsed.courses || [],
          hours: parsed.hours,
          maxHours: parsed.maxHours,
        });
      } catch (error) {
        // skip unparseable entries
      }
      return acc;
    }, []);
  }, [historyDeleted, subsectionId]);

  if (!beforeObject) return null;

  const hasContent = requirements.length > 0 || parsedPreviouslyDeleted.length > 0;
  const isEven = index % 2 === 0;

  return (
    <div
      className={classNames(isEven ? 'bg-gray-2' : 'bg-white', 'border-bottom')}
    >
      <div className="d-flex align-items-center py-2 px-2">
        <div style={{ flex: '1 1 40%' }}>
          <div className="d-flex align-items-center gap-1">
            <span
              className={classNames(hasContent && 'button-link')}
              onClick={
                hasContent
                  ? () => setExpanded(!expanded)
                  : undefined
              }
              style={
                hasContent ? { cursor: 'pointer' } : undefined
              }
            >
              {beforeObject.subsectionName || ''}
            </span>
            {hasContent && (
              <FontAwesomeIcon
                icon={faChevronDown}
                className={classNames(expanded && 'rotate-180')}
                style={{ cursor: 'pointer', width: '12px' }}
                onClick={() => setExpanded(!expanded)}
              />
            )}
          </div>
        </div>
        <div style={{ flex: '0 0 20%' }}>{item.updatedBy}</div>
        <div style={{ flex: '0 0 30%', textAlign: 'right' }}>
          {new Date(item.updatedOn).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          })}
        </div>
      </div>
      {expanded && hasContent && (
        <div className="ms-2 me-2 ps-2 mb-2 border-start border-gray-3 border-4">
          <div
            className="d-flex justify-content-between fw-bold border-bottom pb-1 mb-1"
            style={{ fontSize: '14px' }}
          >
            <div>Requirement</div>
            <div>Credit Hours</div>
          </div>
          {requirements.map((req, reqIndex) => (
            <div
              key={reqIndex}
              className="d-flex justify-content-between py-1"
              style={{ fontSize: '14px' }}
            >
              <div>
                {getRequirementDescription(req.courses || [], courseLists)}
              </div>
              <div>{req.hours || '\u2014'}</div>
            </div>
          ))}
          {parsedPreviouslyDeleted.map((req, delIndex) => (
            <div
              key={`del-${delIndex}`}
              className="d-flex justify-content-between py-1"
              style={{ fontSize: '14px' }}
            >
              <div>
                {getRequirementDescription(req.courses, courseLists)}
              </div>
              <div>
                {req.maxHours && req.maxHours !== 0 && req.maxHours !== req.hours
                  ? `${req.hours} - ${req.maxHours}`
                  : req.hours || '\u2014'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeletedSubsectionsDetails({ deletedSubsections, historyDeleted, handleClose }) {
  const courseLists = useSelector((state) => state.checksheet.courseLists);

  return (
    <div style={{ width: '700px' }}>
      {/* Close Button */}
      <button
        className="border border-gray-3 rounded-circle lh-1"
        style={{ padding: '7px 10px', float: 'right' }}
        onClick={handleClose}
        aria-label="Close"
      >
        <FontAwesomeIcon
          icon={faXmark}
          width="10px"
          height="10px"
          className="text-gray-7"
        />
      </button>

      <h3>
        <FontAwesomeIcon
          icon={faTrash}
          title="Deleted subsections"
          className="fa-trash-inactive pe-1"
          style={{ width: '25px' }}
        />
        Deleted subsections
      </h3>

      <div>
        {/* Header */}
        <div
          className="d-flex fw-bold px-2 py-1 border-bottom"
          style={{ whiteSpace: 'nowrap' }}
        >
          <div style={{ flex: '1 1 40%' }}>Subsection Name</div>
          <div style={{ flex: '0 0 20%' }}>Deleted By</div>
          <div style={{ flex: '0 0 30%', textAlign: 'right' }}>
            Date and time
          </div>
        </div>
        {/* Rows */}
        {deletedSubsections.map((item, index) => (
          <SubsectionRow
            key={index}
            item={item}
            index={index}
            courseLists={courseLists}
            historyDeleted={historyDeleted}
          />
        ))}
      </div>
    </div>
  );
}

export default DeletedSubsectionsDetails;
